/**
 * A styled Input
 */
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import {
  space, fontSize, fontWeight,
} from 'styled-system';
import theme from 'styled-theming';
import { Box } from 'rebass/styled-components';
import { borderRadiusMap, InputShape } from '.';

const placeholderColor = theme('mode', {
  light: (props: any) => props.theme.colors.deepGrey,
  dark: (props: any) => props.theme.colors.whiteGrey,
});

const textColor = theme('mode', {
  light: (props: any) => props.theme.colors.black,
  dark: (props: any) => props.theme.colors.white,
});

const backgroundColor = theme('mode', {
  light: (props: any) => props.theme.colors.white,
  dark: (props: any) => props.theme.colors.lpGhost,
});

const borderColor = theme('mode', {
  light: (props: any) => props.theme.colors.greyLight,
  dark: 'rgba(255, 255, 255, 0.22)',
});

const errorBorderColor = theme('mode', {
  light: (props: any) => props.theme.colors.red,
  dark: (props: any) => props.theme.colors.redLight,
});

const hoverBorderColor = theme('mode', {
  light: (props: any) => props.theme.colors.periwinkle,
  dark: (props: any) => props.theme.colors.brightCerulean,
});

interface IStyledInputPropsExtra {
  isLeftAdornment?: boolean;
  isRightAdornment?: boolean;
  shape: InputShape;
  placeholder: string;
  isErrored?: boolean;
  isLeftInteriorIcon?: boolean;
  isRightInteriorIcon?: boolean;
}

type IStyledInputProps = IStyledInputPropsExtra & React.HTMLProps<HTMLInputElement>;

export const StyledInput = styled.input<IStyledInputProps>`
  ${space}
  ${fontSize}
  ${fontWeight}
  border-top-left-radius: ${({ isLeftAdornment, shape }: IStyledInputProps) => (isLeftAdornment ? '0px' : borderRadiusMap[shape])};
  border-bottom-left-radius: ${({ isLeftAdornment, shape }: IStyledInputProps) => (isLeftAdornment ? '0px' : borderRadiusMap[shape])};
  border-top-right-radius: ${({ isRightAdornment, shape }: IStyledInputProps) => (isRightAdornment ? '0px' : borderRadiusMap[shape])};
  border-bottom-right-radius: ${({ isRightAdornment, shape }: IStyledInputProps) => (isRightAdornment ? '0px' : borderRadiusMap[shape])};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-left: ${({ isLeftInteriorIcon }: IStyledInputProps) => (isLeftInteriorIcon ? '32px' : '8px')};
  padding-right: ${({ isRightInteriorIcon }: IStyledInputProps) => (isRightInteriorIcon ? '32px' : '8px')};
  flex: 1;
  line-height: ${(props) => props.theme.lineHeights.input};
  border: 1px solid ${(props) => (props.isErrored ? errorBorderColor : borderColor)};
  background-color: ${(props) => (props.disabled ? 'transparent' : backgroundColor)};
  color: ${textColor};
  /* Webkit autofill overrides */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    box-shadow: 0 0 1000px ${(props) => props.theme.colors.reallyLightBlue} inset;
    border: 1px solid ${(props) => props.theme.colors.reallyLightPurple};
  }
  font-family: inherit;
  &::placeholder {
    color: ${placeholderColor};
    font-style: italic;
    opacity: 0.4;
  }
  width: 100%;
  outline: none;
  box-shadow: inset -1px -1px 1px rgba(255, 255, 255, 0.11), inset 1px 1px 2px rgba(0, 0, 0, 0.11);
  &:focus {
    border: 1px solid ${hoverBorderColor};
  }
`;

export interface IInputProps {
  onChange: (ev: any) => void;
  value: string;
  placeholder: string;
  shape?: InputShape;
  // This boolean error indicator is widely used throughout the app.
  // Consider a refactor to pass an array of objects containing error messages etc.
  isErrored?: boolean;
  testId?: string;
  [key: string]: any; // any more props :|
}

const InputV2: FunctionComponent<IInputProps> = ({
  onChange, value, placeholder, shape = InputShape.ROUND, isErrored, testId = 'text-field', ...rest
}: IInputProps) => (
  <Box sx={{
    position: 'relative',
    width: '100%',
    height: '100%',
  }}
  >
    <StyledInput
      data-testid={testId}
      onChange={onChange}
      value={value || ''}
      type="text"
      shape={shape}
      placeholder={placeholder}
      isErrored={isErrored}
      {...rest}
    />
  </Box>
);

export default InputV2;
